import React from 'react';
import styled from 'styled-components/macro';
import { Box, Tab, Tabs, Tooltip } from '@material-ui/core';
import { translations } from '../../../../locales/translations';
import { useTranslation } from 'react-i18next';
import { RecurringItems } from './RecurringItems';
import { OneOffItems } from './OneOffItems';
import { DepositItems } from './DepositItems';
import { OtherReimbursementItems } from './OtherReimbursementItems';
import { commonStyle } from '../../../../styles/theme/themes';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectAccessRight,
  selectContractStatus,
} from '../../WorkListPage/slice/selectors';
import { biStatus, biType } from '../../../../locales/types';
import { LoadingIndicator } from '../../../components/LoadingIndicator';
import { useWorkListStateSlice } from '../../WorkListPage/slice';
import {
  selectContractNumber,
  selectOneOff,
  selectOneOffView,
  selectPreviewMode,
  selectRecurring,
  selectRecurringView,
} from '../slice/selectors';
import { toPathSegment } from '../../../../utils/contractNumber';
import DoneOutlinedIcon from '@mui/icons-material/DoneOutlined';
import AssignmentLateOutlinedIcon from '@mui/icons-material/AssignmentLateOutlined';
import AssignmentTurnedInOutlinedIcon from '@mui/icons-material/AssignmentTurnedInOutlined';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import { RecurringData } from '../slice/types';
import { apiList } from '../../../../locales/apiList';
import { UseAxios } from '../../../../utils/UseAxios';
import { TabWithTooltip } from 'app/components/TabWithTooltip';

function TabPanel(props: {
  children?: React.ReactNode;
  selectedTab: biType;
  tab: biType;
}) {
  const { children, selectedTab, tab, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={selectedTab !== tab}
      id={`simple-tabpanel-${tab}`}
      aria-labelledby={`simple-tab-${tab}`}
      {...other}
    >
      {selectedTab === tab && (
        <Box sx={{ p: 3 }}>
          <div>{children}</div>
        </Box>
      )}
    </div>
  );
}

export function BillingItemsTab() {
  const { t } = useTranslation();
  const [selectedTab, setSelectedTab] = React.useState<biType>(biType.RECURRING);
  const { actions } = useWorkListStateSlice();
  const dispatch = useDispatch();
  const contractNumber = useSelector(selectContractNumber);
  const contractStatus = useSelector(selectContractStatus);
  const recurringData: RecurringData | undefined = useSelector(selectRecurring);
  const oneOffData = useSelector(selectOneOff);
  const recurringView = useSelector(selectRecurringView);
  const oneOffView = useSelector(selectOneOffView);
  const previewMode = useSelector(selectPreviewMode);
  const showRecurring = Boolean(recurringData || (previewMode && recurringView));
  const showOneOff = Boolean(oneOffData || (previewMode && oneOffView));
  const accessright = useSelector(selectAccessRight);
  const [recurringStatusIcon, setRecurringStatusIcon] =
    React.useState<JSX.Element>(<div />);
  const [oneOffStatusIcon, setOneOffStatusIcon] = React.useState<JSX.Element>(
    <div />,
  );
  const [depositStatusIcon, setDepositStatusIcon] = React.useState<JSX.Element>(
    <div />,
  );

  React.useEffect(() => {
    UseAxios(`${apiList.getWorklistByIDURL}${toPathSegment(contractNumber)}`, 'get')
      .then(function (response) {
        dispatch(actions.updateContractStatus(response.data));
      })
      .catch(function (error) {
        console.log('error: ' + JSON.stringify(error));
        dispatch(actions.updateErrorWordings(translations.error.commonError));
        dispatch(actions.updateErrorDialog(true));
      });
  }, []);

  React.useEffect(() => {
    if (selectedTab === biType.RECURRING && !showRecurring) {
      const nextTab = showOneOff ? biType.ONEOFF : biType.DEPOSIT;
      setSelectedTab(nextTab);
      dispatch(actions.updateSelectedPreviewSection(nextTab));
    } else if (selectedTab === biType.ONEOFF && !showOneOff) {
      const nextTab = showRecurring ? biType.RECURRING : biType.DEPOSIT;
      setSelectedTab(nextTab);
      dispatch(actions.updateSelectedPreviewSection(nextTab));
    }
  }, [showRecurring, showOneOff]);

  React.useEffect(() => {
    if (contractStatus) {
      updateStatusIcons(contractStatus);
    }
  }, [contractStatus]);

  function updateStatusIcons(statusData) {
    if (accessright.edit) {
      setRecurringStatusIcon(mapStatusToIcon(statusData.recurringStatus));
      setOneOffStatusIcon(mapStatusToIcon(statusData.oneOffStatus));
      setDepositStatusIcon(mapStatusToIcon(statusData.depositStatus));
    }
  }

  function mapStatusToIcon(status: biStatus) {
    switch (status) {
      case biStatus.PENDING:
        return <AssignmentLateOutlinedIcon style={{ fill: 'red' }} />;
      case biStatus.COMPLETE:
        return <AssignmentTurnedInOutlinedIcon />;
      case biStatus.TBC:
        return <PendingActionsIcon />;
      case biStatus.AUTOComplete:
        return <DoneOutlinedIcon />;
      default:
        return <div />;
    }
  }

  const handleTabChange = (_event, nextTab: biType) => {
    setSelectedTab(nextTab);
    dispatch(actions.updateSelectedPreviewSection(nextTab));
  };

  function a11yProps(tab: biType) {
    return {
      id: `simple-tab-${tab}`,
      'aria-controls': `simple-tabpanel-${tab}`,
    };
  }

  return (
    <Wrapper>
      {contractStatus ? (
        <Box className="tab-container">
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
              className="tabs-container"
              value={selectedTab}
              onChange={handleTabChange}
              textColor="primary"
              indicatorColor="primary"
            >
              {showRecurring && (
                  <TabWithTooltip
                    tooltip={
                      (accessright.edit && contractStatus.recurringStatus === biStatus.AUTOComplete)
                        ? contractStatus?.statusReasons?.recurring
                        : ''
                    }
                    label={t(translations.biType.recurring)}
                    value={biType.RECURRING}
                    {...a11yProps(biType.RECURRING)}
                    icon={recurringStatusIcon}
                  />
              )}
              {showOneOff && (
                
                  <TabWithTooltip
                    tooltip={
                      (accessright.edit && contractStatus.oneOffStatus === biStatus.AUTOComplete)
                        ? contractStatus?.statusReasons?.oneOff
                        : ''
                    }
                    label={t(translations.biType.oneOff)}
                    value={biType.ONEOFF}
                    {...a11yProps(biType.ONEOFF)}
                    icon={oneOffStatusIcon}
                  />
              )}
              
                <TabWithTooltip
                  tooltip={
                    (accessright.edit && contractStatus.depositStatus === biStatus.AUTOComplete)
                      ? contractStatus?.statusReasons?.deposit
                      : ''
                  }
                  label={t(translations.biType.deposit)}
                  value={biType.DEPOSIT}
                  {...a11yProps(biType.DEPOSIT)}
                  icon={depositStatusIcon}
                />
              <Tab
                label={t(translations.contractDetail.otherReimburse)}
                value={biType.OTHERREIMBURSEMENT}
                {...a11yProps(biType.OTHERREIMBURSEMENT)}
              />
            </Tabs>
          </Box>
          {showRecurring && (
            <TabPanel selectedTab={selectedTab} tab={biType.RECURRING}>
              <RecurringItems recurringData={recurringData} />
            </TabPanel>
          )}
          {showOneOff && (
            <TabPanel selectedTab={selectedTab} tab={biType.ONEOFF}>
              <OneOffItems oneOffData={oneOffData} />
            </TabPanel>
          )}
          <TabPanel selectedTab={selectedTab} tab={biType.DEPOSIT}>
            <DepositItems />
          </TabPanel>
          <TabPanel selectedTab={selectedTab} tab={biType.OTHERREIMBURSEMENT}>
            <OtherReimbursementItems />
          </TabPanel>
        </Box>
      ) : (
        <LoadingIndicator />
      )}
    </Wrapper>
  );
}

const Wrapper = styled.div`
  width: 100%;
  border-radius: 5px;
  margin-top: 10px;
  box-shadow: ${commonStyle.boxShadow};

  .MuiTab-root {
    border-width: thin;
  }

  .MuiTab-wrapper {
    display: initial;
  }

  .tab-container {
    .MuiBox-root {
      padding: 1px 5px 5px !important;
    }
  }

  .MuiTab-textColorPrimary.Mui-selected {
    background-color: ${p => p.theme.background};
  }
`;
